/**
 * PhoneInputField — wraps react-phone-number-input
 * - No default country: the user must explicitly pick their country before
 *   entering digits, so we never silently assume a country (e.g. India) for
 *   users based elsewhere.
 * - Value is always E.164 format: "+919876543210"
 * - Validates with libphonenumber-js on blur
 * - Same interface as before so existing callers don't change
 */

import React, { useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import InfoTip from "./InfoTip";

interface PhoneInputFieldProps {
  id?: string;
  label: string;
  infoTip?: string;
  value: string;                 // E.164, e.g. "+919876543210"
  onChange: (e164: string) => void;
  required?: boolean;
  placeholder?: string;
  defaultCountryIso2?: string;   // BCP-47 country code, e.g. "IN" — omit to force explicit choice
  className?: string;
  error?: string;                // external error from parent / server
  disabled?: boolean;            // read-only display (e.g. the locked account WhatsApp number)
  hint?: string;                 // helper text under the field
}

const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
  id,
  label,
  infoTip,
  value,
  onChange,
  required,
  placeholder = "Phone number",
  defaultCountryIso2,
  className = "",
  error,
  disabled = false,
  hint,
}) => {
  const [touched, setTouched] = useState(false);

  // Inline validation — only show after user has blurred the field
  const inlineError =
    touched && value && !isValidPhoneNumber(value)
      ? "Enter a valid phone number including country code"
      : null;

  const displayError = error || inlineError;

  return (
    <div className={className}>
      {label && (
        <div className="mb-1.5 flex items-center gap-1">
          <label className="label mb-0" htmlFor={id}>
            {label}
            {required && <span className="ml-0.5 text-red-500">*</span>}
          </label>
          {infoTip && <InfoTip message={infoTip} />}
        </div>
      )}

      <div
        className={`phone-field-wrapper rounded-lg border bg-surface ${
          displayError ? "border-red-400 ring-1 ring-red-400" : "border-border"
        } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
      >
        <PhoneInput
          id={id}
          international
          defaultCountry={defaultCountryIso2 as import("react-phone-number-input").Country}
          value={value || undefined}
          onChange={(v) => onChange(v ?? "")}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          countryCallingCodeEditable={false}
          limitMaxLength
          disabled={disabled}
        />
      </div>

      {hint && !displayError && (
        <p className="mt-1 text-xs text-foreground-muted">{hint}</p>
      )}

      {displayError && (
        <p className="mt-1 text-xs text-red-500">{displayError}</p>
      )}
    </div>
  );
};

export default PhoneInputField;
